import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { BedDouble, Bath, Users, MapPin, Phone, Mail, Check, X, ArrowRight, CalendarDays, Clock } from "lucide-react";
import { SITE, HOUSE_RULES } from "@/data/site";
import useProperties from "@/hooks/useProperties";
import { getPhotos, getLibraryManaged, photoSrc } from "@/lib/api";
import EnquiryForm from "@/components/EnquiryForm";
import Reviews from "@/components/Reviews";
import HeroSlider from "@/components/HeroSlider";
import BookedDates from "@/components/BookedDates";
import Seo from "@/components/Seo";

export default function PropertyDetail() {
  const { slug } = useParams();
  const properties = useProperties();
  const property = properties.find((p) => p.slug === slug);
  const [uploaded, setUploaded] = useState([]);
  const [libraryManaged, setLibraryManaged] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const jsonLd = useMemo(
    () =>
      property
        ? {
            "@context": "https://schema.org",
            "@type": "VacationRental",
            name: property.name,
            description: property.short,
            address: { "@type": "PostalAddress", addressLocality: property.location, addressCountry: "AU" },
            numberOfBedrooms: property.bedrooms,
            occupancy: { "@type": "QuantitativeValue", value: property.sleeps },
            image: property.hero,
            telephone: SITE.phone,
          }
        : null,
    [property]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!property) return;
    let active = true;
    // Asked separately because it counts deleted rows too: once the owner has taken control of a
    // property's photos, deleting them all must leave an empty gallery rather than silently
    // bringing the original built-in photos back.
    Promise.all([getPhotos(slug), getLibraryManaged(slug)])
      .then(([photos, managed]) => {
        if (!active) return;
        setLibraryManaged(Boolean(managed.data?.managed));
        setUploaded(photos.data.map((p) => ({ src: photoSrc(p), caption: p.caption || "" })));
      })
      .catch(() => {
        if (!active) return;
        setLibraryManaged(false);
        setUploaded([]);
      });
    return () => {
      active = false;
    };
  }, [slug, property]);

  if (!property) return <Navigate to="/" replace />;

  const gallery = libraryManaged
    ? uploaded
    : [...property.gallery.map((src) => ({ src, caption: "" })), ...uploaded];
  const others = properties.filter((p) => p.slug !== slug);
  // Hero shows the main image first, then the next gallery photos (8 max)
  const heroSlides = [property.hero, ...gallery.map((g) => g.src).filter((src) => src !== property.hero)].slice(0, 8);

  return (
    <main data-testid="property-page">
      <Seo
        title={property.seoTitle || `PARADISIO RESORTS | ${property.name} — ${property.location}`}
        description={
          property.seoDescription ||
          `${property.short} ${property.bedrooms} bedrooms, ${property.bathrooms} bathrooms, sleeps ${property.sleeps}. Enquire directly with owner Robert.`
        }
        image={property.hero}
        imageAlt={property.heroAlt || `${property.name}, ${property.location}`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative">
        <HeroSlider images={heroSlides} alt={property.heroAlt || `${property.name}, ${property.location}`} />
        <div className="absolute inset-0 flex items-end pointer-events-none">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-10 md:pb-16">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#FCFAF6]/85">{property.location}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FCFAF6] mt-4 max-w-3xl leading-[1.03]">
              {property.name}
            </h1>
            {property.tagline && (
              <p className="text-base md:text-lg text-[#FCFAF6]/90 mt-4 max-w-2xl" data-testid="property-tagline">
                {property.tagline}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Facts */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-b border-[#0F2A52]/10">
        <div className="flex flex-wrap items-center gap-8 md:gap-14" data-testid="property-facts">
          <span className="flex items-center gap-3 text-sm text-[#1A1A1A]">
            <BedDouble size={18} className="text-[#B08A3E]" /> {property.bedrooms}{" "}
            {property.bedrooms === 1 ? "bedroom" : "bedrooms"}
          </span>
          <span className="flex items-center gap-3 text-sm text-[#1A1A1A]">
            <Bath size={18} className="text-[#B08A3E]" /> {property.bathrooms}{" "}
            {property.bathrooms === 1 ? "bathroom" : "bathrooms"}
          </span>
          <span className="flex items-center gap-3 text-sm text-[#1A1A1A]">
            <Users size={18} className="text-[#B08A3E]" /> sleeps {property.sleeps}
          </span>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left */}
        <div className="min-w-0 lg:col-span-8">
          <h2 className="font-display text-4xl text-[#0F2A52]">About this home</h2>
          {property.description.map((para) => (
            <p key={para} className="text-base text-[#4A4A4A] leading-relaxed mt-5">
              {para}
            </p>
          ))}

          {/* Gallery */}
          <h2 className="font-display text-4xl text-[#0F2A52] mt-16">Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8" data-testid="property-gallery">
            {(showAll ? gallery : gallery.slice(0, 12)).map((g, i) => (
              <button
                key={g.src + i}
                onClick={() => setLightbox(g)}
                className="overflow-hidden aspect-[4/3] group"
                data-testid={`gallery-image-${i}`}
              >
                <img
                  src={g.src}
                  alt={g.caption || `${property.name} holiday accommodation, ${property.location} — photo ${i + 1} of ${gallery.length}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </button>
            ))}
          </div>
          {gallery.length > 12 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-6 border border-[#0F2A52]/30 px-6 py-3 text-sm text-[#0F2A52] hover:bg-[#F2EBDD] transition-colors duration-200"
              data-testid="gallery-show-all"
            >
              {showAll ? "Show fewer photos" : `Show all ${gallery.length} photos`}
            </button>
          )}

          {/* Features */}
          <h2 className="font-display text-4xl text-[#0F2A52] mt-16">Key features</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 mt-8">
            {property.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-[#4A4A4A]">
                <Check size={16} className="text-[#B08A3E] mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>

          <h2 className="font-display text-4xl text-[#0F2A52] mt-16">Amenities</h2>
          <ul className="flex flex-wrap gap-3 mt-8" data-testid="property-amenities">
            {property.amenities.map((a) => (
              <li key={a} className="border border-[#0F2A52]/15 px-4 py-2 text-xs text-[#4A4A4A]">
                {a}
              </li>
            ))}
          </ul>

          {/* House rules */}
          <h2 className="font-display text-4xl text-[#0F2A52] mt-16">Stay details &amp; house rules</h2>
          <div className="mt-8 border border-[#0F2A52]/15 bg-[#F2EBDD] p-6 md:p-8" data-testid="house-rules">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: CalendarDays, label: "Minimum stay", value: property.minimumStay || HOUSE_RULES.minimumStay },
                { icon: Clock, label: "Arrival", value: HOUSE_RULES.checkIn },
                { icon: Clock, label: "Departure", value: HOUSE_RULES.checkOut },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label}>
                  <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#B08A3E]">
                    <Icon size={14} /> {label}
                  </p>
                  <p className="text-sm text-[#1A1A1A] leading-relaxed mt-2">{value}</p>
                </div>
              ))}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 mt-8 pt-8 border-t border-[#0F2A52]/15">
              {HOUSE_RULES.rules.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm text-[#4A4A4A]">
                  <Check size={15} className="text-[#B08A3E] mt-0.5 shrink-0" /> {r}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#4A4A4A]/85 leading-relaxed mt-6">
              Exact rates depend on your dates and length of stay — Robert confirms the total, the deposit and
              payment details with you by email or phone before anything is paid.
            </p>
          </div>

          <BookedDates propertySlug={property.slug} />

          {/* Reviews */}
          <Reviews propertySlug={property.slug} />

          {/* Location */}
          <h2 className="font-display text-4xl text-[#0F2A52] mt-16">Location</h2>
          <p className="text-base text-[#4A4A4A] leading-relaxed mt-5 flex items-start gap-3">
            <MapPin size={18} className="text-[#B08A3E] mt-1 shrink-0" /> {property.locationNote}
          </p>
          {property.areaPhotos?.length > 0 && (
            <div className="mt-8" data-testid="area-photos">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {property.areaPhotos.map((a) => (
                  <figure key={a.src} className="overflow-hidden tile-hover group">
                    <img
                      src={a.src}
                      alt={a.alt}
                      loading="lazy"
                      className="w-full h-32 md:h-40 object-cover"
                    />
                    {a.label && (
                      <figcaption className="text-[11px] uppercase tracking-[0.18em] text-[#4A4A4A] mt-2">
                        {a.label}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
              <p className="text-[11px] text-[#4A4A4A]/70 mt-4">
                Kirra Beach and the Coolangatta coastal path, moments from the door.
              </p>
            </div>
          )}

          <div className="mt-8 border border-[#0F2A52]/15">
            <iframe
              title={`Map of ${property.name}`}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(property.location)}&z=13&output=embed`}
              className="w-full h-[320px]"
              loading="lazy"
              data-testid="property-map"
            />
          </div>
        </div>

        {/* Sticky enquiry */}
        <aside className="min-w-0 lg:col-span-4">
          <div className="lg:sticky lg:top-28 border border-[#0F2A52]/15 p-7 md:p-8 bg-[#F2EBDD]">
            <h2 className="font-display text-3xl text-[#0F2A52]">Enquire about this home</h2>
            <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">
              Direct with Robert. He'll confirm availability and the total for your dates.
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <a
                href={`tel:${SITE.phone}`}
                className="inline-flex items-center justify-center gap-3 bg-[#0F2A52] text-[#FCFAF6] px-6 py-4 text-sm hover:-translate-y-1 hover:shadow-lg transition-transform duration-200"
                data-testid="property-call-btn"
              >
                <Phone size={16} /> Call {SITE.phoneDisplay}
              </a>
              <a
                href={`mailto:${SITE.email}?subject=Enquiry: ${encodeURIComponent(property.name)}`}
                className="inline-flex items-center justify-center gap-3 border border-[#0F2A52]/30 px-6 py-4 text-sm text-[#0F2A52] hover:bg-[#FCFAF6] transition-colors duration-200"
                data-testid="property-email-btn"
              >
                <Mail size={16} /> Email Robert
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-[#0F2A52]/15">
              <EnquiryForm propertySlug={property.slug} compact />
            </div>
          </div>
        </aside>
      </section>

      {/* Other properties */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24">
        <h2 className="font-display text-4xl text-[#0F2A52]">The other Paradisio homes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
          {others.map((p) => (
            <Link
              key={p.slug}
              to={`/stay/${p.slug}`}
              className="group border border-[#0F2A52]/10"
              data-testid={`related-${p.slug}`}
            >
              <div className="overflow-hidden aspect-[4/3]">
                <img
                  src={p.hero}
                  alt={p.heroAlt || `${p.name} holiday accommodation, ${p.location}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl text-[#0F2A52]">{p.name}</h3>
                <p className="text-xs text-[#4A4A4A] mt-2">{p.location}</p>
                <span className="inline-flex items-center gap-2 text-sm text-[#B08A3E] mt-5 group-hover:gap-4 transition-all duration-300">
                  View <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <button className="absolute top-6 right-6 text-white" data-testid="lightbox-close" aria-label="Close">
            <X size={28} />
          </button>
          <div className="flex flex-col items-center gap-4">
            <img src={lightbox.src} alt={lightbox.caption || `${property.name}, ${property.location}`} className="max-h-[82vh] max-w-full object-contain" />
            {lightbox.caption && (
              <p className="text-sm text-[#FCFAF6]/85 text-center max-w-2xl" data-testid="lightbox-caption">
                {lightbox.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
